declare namespace sp {
    interface Skeleton {
        addChildFollowSlot(slot: string, node: cc.Node)
        addChildFollowBone(bone: string, node: cc.Node)
        findFollowSlot(slot: string): cc.Node
        findFollowBone(bone: string): cc.Node
        removeChildFollowSlot(slot: string)
        removeChildFollowBone(bone: string)
        getBonePosition(bone: string): cc.Vec2
        _updateFollowBone()
        onSpineLoaded();
    }
}

const Skeleton = sp.Skeleton

const EmptyHandle = function () { }
const limitNode = function (node) {
    // attached node's world matrix update per frame
    Object.defineProperty(node, '_worldMatDirty', {
        get() { return true; },
        set(value) {/* do nothing */ }
    });
    // shield world matrix calculate interface
    node._calculWorldMatrix = EmptyHandle;
    node._mulMat = EmptyHandle;
};

Object.defineProperty(Skeleton.prototype, `followSlotMap`, {
    set(followSlotMap) {
        this._followSlotMap = followSlotMap
    },
    get() {
        if (this._followSlotMap == null) {
            this._followSlotMap = {}
        }
        return this._followSlotMap;
    },
    configurable: true
});

Object.defineProperty(Skeleton.prototype, `followBoneMap`, {
    set(followBoneMap) {
        this._followBoneMap = followBoneMap
    },
    get() {
        if (this._followBoneMap == null) {
            this._followBoneMap = {}
        }
        return this._followBoneMap;
    },
    configurable: true
});

Skeleton.prototype.findFollowSlot = function (slotName: string) {
    return this.followSlotMap[slotName];
}

Skeleton.prototype.findFollowBone = function (boneName: string) {
    return this.followBoneMap[boneName];
}

Skeleton.prototype.addChildFollowSlot = function (slotName: string, node: cc.Node) {
    let preNode = this.followSlotMap[slotName];
    if (preNode) {
        preNode.removeFromParent();
    }

    limitNode(node);
    this.followSlotMap[slotName] = node;
    node.parent = this.node;
    node["_renderFlag"] |= cc["RenderFlow"].FLAG_BREAK_FLOW;
    node.name = slotName;
    node.x = 0;
    node.y = 0;
    if (this._skeleton && this._skeleton.addSlotBindNode) {
        this._skeleton.addSlotBindNode(slotName);
    }
}

Skeleton.prototype.removeChildFollowSlot = function (slotName: string) {
    let node = this.followSlotMap[slotName];
    if (node) {
        node.removeFromParent();
    }
    delete this.followSlotMap[slotName];
    if (this._skeleton && this._skeleton.removeSlotBindNode) {
        this._skeleton.removeSlotBindNode(slotName);
    }
}

Skeleton.prototype.addChildFollowBone = function (boneName: string, node: cc.Node) {
    let preNode = this.followBoneMap[boneName];
    if (preNode) {
        preNode.removeFromParent();
    }

    this.followBoneMap[boneName] = node;
    node.parent = this.node;
}

Skeleton.prototype.removeChildFollowBone = function (boneName: string) {
    let node = this.followBoneMap[boneName];
    if (node) {
        node.removeFromParent();
    }
    delete this.followBoneMap[boneName];
}

const orgSetAnimation = Skeleton.prototype.setAnimation
Skeleton.prototype.setAnimation = function (trackIndex: number, name: string, loop: boolean) {
    if (name == null || name == "") {
        cc.error("spine animation name can not be empty");
        return null;
    }
    this["@waitSetAnimation"] = null
    if (this._skeleton == null) {
        // CC_DEV && cc.warn('setAnimation: this._skeleton is null', name, loop, this["@path"])
        this["@waitSetAnimation"] = { trackIndex, name, loop }
        return null
    }
    return orgSetAnimation.call(this, trackIndex, name, loop)
}

const orgSetSkeletonData = Skeleton.prototype['setSkeletonData']
Skeleton.prototype['setSkeletonData'] = function (skeletonData) {
    orgSetSkeletonData.call(this, skeletonData)

    if (this._skeleton && cc.sys.isNative) {
        let keys = Object.keys(this.followSlotMap);
        if (keys.length <= 0) return;
        keys.forEach((slotName) => {
            if (this._skeleton && this._skeleton.addSlotBindNode) {
                this._skeleton.addSlotBindNode(slotName);
            }
        })
    }
}


const orgUpdate = Skeleton.prototype['update']
Skeleton.prototype['update'] = function (dt) {
    orgUpdate.call(this, dt)
    this._updateFollowBone();
}


Skeleton.prototype._updateFollowBone = function () {
    let keys = Object.keys(this.followBoneMap);

    if (keys.length <= 0) return;

    if (this._skeleton) {
        let bone, node;
        for (let i = 0, n = this._skeleton.bones.length; i < n; i++) {
            bone = this._skeleton.bones[i];
            node = this.followBoneMap[bone.data.name];

            if (node) {
                node.setPosition(bone.worldX, bone.worldY);
                node.angle = bone.rotation;
            }
        }
    }
}

Skeleton.prototype.getBonePosition = function (boneName: string) {
    if (this._skeleton) {
        var bone = this.findBone(boneName);

        if (bone) {
            var node = this.node;
            return cc.v2(bone.worldX * node.scaleX, bone.worldY * node.scaleY);
        }
    }
    return cc.v2(-1, -1);
}

Skeleton.prototype.onSpineLoaded = function () {
    if (this._skeleton && cc.sys.isNative) {
        let keys = Object.keys(this.followSlotMap);
        if (keys.length <= 0) return;
        let removeKeys = [];
        keys.forEach((slotName) => {
            let slot = this.findSlot(slotName);
            if (slot == null) {
                removeKeys.push(slotName);
            }
        })

        removeKeys.forEach((slotName) => {
            this.removeChildFollowSlot(slotName);
        })
    }

    if (this["@waitSetAnimation"]) {
        let { trackIndex, name, loop } = this["@waitSetAnimation"]
        // CC_DEV && cc.warn("onSkeletonLoaded", name, loop, this["@path"])
        this.setAnimation(trackIndex, name, loop)
    }
}

const AnimationState = sp.spine.AnimationState

const orgTrackEntry = AnimationState.prototype.trackEntry
AnimationState.prototype.trackEntry = function (trackIndex, animation, loop, last) {
    var entry = this.trackEntryPool.obtain();
    // entry.reset()
    entry.trackIndex = trackIndex;
    entry.animation = animation;
    entry.loop = loop;
    entry.holdPrevious = false;
    entry.eventThreshold = 0;
    entry.attachmentThreshold = 0;
    entry.drawOrderThreshold = 0;
    entry.animationStart = 0;
    entry.animationEnd = animation.duration;
    entry.animationLast = -1;
    entry.nextAnimationLast = -1;
    entry.delay = 0;
    entry.trackTime = 0;
    entry.trackLast = -1;
    entry.nextTrackLast = -1;
    entry.trackEnd = Number.MAX_VALUE;
    entry.timeScale = 1;
    entry.alpha = 1;
    entry.interruptAlpha = 1;
    entry.mixTime = 0;
    entry.mixDuration = last == null ? 0 : this.data.getMix(last.animation, animation);
    return entry;
};

const orgSetAnimationWith = AnimationState.prototype.setAnimationWith
AnimationState.prototype.setAnimationWith = function (trackIndex, animation, loop) {
    if (animation == null)
        throw new Error("animation cannot be null.");
    var interrupt = true;
    var current = this.expandToIndex(trackIndex);
    if (current != null) {
        //NOTICE: applyMixingFrom 函数会堆栈溢出，一直会循环下去
        if (current.mixingFrom?.mixingFrom) {
            current.mixingFrom.mixingFrom = null;
        }
        if (current.nextTrackLast == -1) {
            this.tracks[trackIndex] = current.mixingFrom;
            this.queue.interrupt(current);
            this.queue.end(current);
            this.disposeNext(current);
            current = current.mixingFrom;
            interrupt = false;
        }
        else
            this.disposeNext(current);
    }
    var entry = this.trackEntry(trackIndex, animation, loop, current);
    this.setCurrent(trackIndex, entry, interrupt);
    this.queue.drain();
    return entry;
};